import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { Subject } from 'rxjs';
import { ScheduledEventByOccurrenceSupportHoursQueryResult } from 'src/app/shared/graphql/sessions/scheduled-event-by-occurrence-query.graphql';
import { HealthChangeRequest } from 'src/app/shared/models/gql/health-change-request.model';
import { SupportHealthChangeQuestionnaire } from 'src/app/shared/models/gql/support-health-change-questionnaire.model';
import { SupportHealthChange } from 'src/app/shared/models/gql/support-health-change.model';
import { pageInfoFragment } from '../gql-fragments/gql-fragments';
import { ShiftNote } from '../models/gql/shift-note.model';
import { PaginatedQueryPage } from '../models/gql/special/paginated-query-page.model';
import { Timesheet } from '../models/gql/timesheet.model';
import { InvoiceExpressPayStatement } from '../models/invoice-express-pay-statement';
import { ApiInterfaceService } from './api-interface.service';

@Injectable({
  providedIn: 'root',
})
export class TimesheetsService {
  // when fired, this lets subscribers know that the timesheet list needs refreshing.
  public refreshList: Subject<void> = new Subject();

  constructor(
    private api: ApiInterfaceService,
  ) {
  }

  /**
   * Fire an event to let other pages know that they need to refresh their timesheet data.
   *
   */
  refresh() {
    this.refreshList.next();
  }

  /**
   * Sends a request for fetching timesheets.
   *
   * @param onlyWithHealthMoodChanges - flag that we need to pass based on selected filter type for current support hours.
   * @param filter - Filter that we need to pass based on selected tab.
   * @param after - It will contain endCursor of page info returned from recent query.
   * @param includeDeliveredSessions - Flag to include the event deliveries that has a delivered session.
   */
  async getTimesheetByStatus(filter, after, onlyWithHealthMoodChanges: boolean, includeDeliveredSessions: boolean): Promise<PaginatedQueryPage<(Timesheet)>> {
    try {
      const res = await this.api.gqlQuery(gql`
        query eventDeliveries($filter: String, $after: String, $first: Int, $onlyWithHealthMoodChanges: Boolean, $includeDeliveredSessions: Boolean = true) {
          eventDeliveries(onlyWithHealthMoodChanges: $onlyWithHealthMoodChanges, first:$first, after:$after, filter: $filter, includeDeliveredSessions: $includeDeliveredSessions) {
            nodes {
              id
              agreement {
                otherUser {
                  fullName
                }
              }
              approvedAt
              carerPaidAt
              carerPaidStatus
              deliveredStartTime
              deliveredHoursAndMinutes
              deliveryDate
              horizonOfferStatus
              invoicedAt
              paynowDeclinedAt
              paynowRequestedAt
              paynowRequestedStatus
              paymentDueByDate
              perKmRateAvailable
              quickpayScheduledDate
              state
              workerPaymentQueriedAt
              withInfoBanner
              withSupportHealthChange
              supportHealthChangeCheckedQuestions
              supportHealthChange {
                shareHealthChangeInformation
              }
              hasIncident
              showIncidentToClient
            }

            totalCount

            pageInfo {
              ...PageInfo
            }
          }
        } ${pageInfoFragment}`, {
        first: 20,
        after,
        filter,
        onlyWithHealthMoodChanges,
        includeDeliveredSessions
      });
      return res['eventDeliveries'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  expressPayStatus(timesheet: Timesheet | ShiftNote) {
    timesheet = timesheet as Timesheet;
    if (
      timesheet.paynowRequestedStatus === 'declined'
    ) {
      return 'declined';
    }
    if (
      timesheet.carerPaidStatus === 'paynow_available'
    ) {
      return 'available';
    }
    if (
      timesheet.carerPaidStatus === 'paynow_requested'
    ) {
      return 'requested';
    }
    if (
      timesheet.carerPaidStatus === 'paynow_paid'
    ) {
      return 'paid';
    }
  }

  /**
   * Check if the timesheet horizon one offer is in a state that requires the H1 banner to be shown.
   *
   * @param timesheet - The timesheet to be checked.
   * @param isHorizonOfferApproved - Specifies whether to check if the horizon one offer is approved.
   * @returns - True if the banner needs to be shown, otherwise false.
   */
  showHorizonOneBanner(timesheet: Timesheet | ShiftNote | ScheduledEventByOccurrenceSupportHoursQueryResult, isHorizonOfferApproved = false) {
    timesheet = timesheet as Timesheet;
    return timesheet?.withInfoBanner
      && timesheet?.approvedAt
      && timesheet?.state !== 'delivered' && (
    // when isHorizonOfferApproved is true then return true when H1 status is approved but timesheet is not invoiced
      (isHorizonOfferApproved && timesheet?.horizonOfferStatus === 'approved' && timesheet?.invoicedAt === null)
        || timesheet?.horizonOfferStatus === 'declined'
        || timesheet?.horizonOfferStatus === 'escalated'
        || timesheet?.horizonOfferStatus === 'pending_review'
      || timesheet?.horizonOfferStatus === 'approved_with_warning'
    );
  }

  /**
   * Sends a request for new timesheet.
   *
   * @param timesheet - Timesheet params object for validate new timesheet.
   */
  async newTimesheet(timesheet) {
    try {
      const res = await this.api.gqlMutation(gql`
      mutation newTimesheet($NewTimesheetInput: NewTimesheetInput!) {
        newTimesheet(input: $NewTimesheetInput) {
          timesheet {
            consumer {
              isHcpFunded
              id
              fullName
              clientAccountType
              participants {
                fullName
                firstName
              }
              firstName
              organisation {
                businessName
              }
              ndisFunded
              privatlyFunded
              uuid
            }
            agreement {
              id
              otherUser {
                fullName
                firstName
                id
                uuid
                isHcpFunded
                ndisAgencyManaged
                disablePerKmTravel
              }
              agreementRates {
                rateCents
                flatRate
                rateName
                id
              }
              travelExpenseFlag
              quickpayEnabled
            }
            shiftNote {
              description
            }
            deliveredHoursAndMinutes
            rate
            rateKindLabel
            total
            netHourlyRate
            hourlyFee
            canDeleteAssets
            paynowFeePercentage
            paynowDisplayable
            deliveredCost
            totalFee
            warnings
            agreementRateId
            isPending
            warnEdit
            feePercentage
            hasIncident
            kmTravelled
            perKmRateInDollars
            totalTravelledInDollars
            totalTravelExpense
            totalTravelFee
            totalWithTravelCost
          }
        }
      }`, { NewTimesheetInput: timesheet });
      return res['newTimesheet']['timesheet'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a GET request for get invoice statement by id.
   *
   * @param invoiceStatementId - Id of carer invoice statement.
   */
  async getInvoiceStatementById(invoiceStatementId): Promise<InvoiceExpressPayStatement> {
    try {
      const res = await (<any>(await this.api.get(`/api/v1/workers/billing/paynow_remittance/${invoiceStatementId}.json`, true)).toPromise());
      return res.data;
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }

  /**
   * Send a POST request for request express pay.
   *
   * @param invoiceStatementId - Invoice statement id for send request.
   */
  async requestExpressPay(invoiceStatementId) {
    try {
      const res = await (<any>(await this.api.post(`/carer_invoice_statements/${invoiceStatementId}/express_pay.json`, null, true)).toPromise());
      this.refresh();
      return res;
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }

  /**
   * Sends a request for submit timesheet.
   *
   * @param timesheet - The timesheet object for create new timesheet.
   */
  async create(timesheet): Promise<any> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation createTimesheet($CreateTimesheetInput: CreateTimesheetInput!) {
            createTimesheet(input: $CreateTimesheetInput) {
              timesheet {
                id
                workerFirstDelivered
                deliveredHoursAndMinutes
                rate
              }
            }
          }
        `,
        { CreateTimesheetInput: timesheet },
      );
      return res['createTimesheet']['timesheet'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a request for update timesheet.
   *
   * @param timesheet - The timesheet object for update timesheet.
   */
  async update(timesheet: Partial<Timesheet>): Promise<any> {
    try {
      const res = await this.api.gqlMutation(gql`
      mutation updateTimesheet($UpdateTimesheetInput: UpdateTimesheetInput!) {
        updateTimesheet(input: $UpdateTimesheetInput) {
          timesheet {
            id
          }
        }
      }`, { UpdateTimesheetInput: timesheet });
      return res['updateTimesheet']['timesheet'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a request to delete timesheet.
   *
   * @param id - Id for delete timesheet.
   * @param timesheetId
   */
  async delete(timesheetId: number): Promise<any> {
    try {
      const res = await this.api.gqlMutation(gql`
      mutation deleteTimesheet($DeleteTimesheetInput: DeleteTimesheetInput!) {
        deleteTimesheet(input: $DeleteTimesheetInput) {
          timesheet {
            id
          }
        }
      }`, { DeleteTimesheetInput: { id: timesheetId } });
      return res['deleteTimesheet']['timesheet'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Send a request for reject timesheet.
   *
   * @param id - Id for reject timesheet.
   * @param timesheetId
   */
  async reject(timesheetId: number): Promise<any> {
    try {
      const res = await this.api.gqlMutation(gql`
      mutation rejectTimesheet($RejectTimesheetInput: RejectTimesheetInput!) {
        rejectTimesheet(input: $RejectTimesheetInput) {
          timesheet {
            id
          }
        }
      }`, { RejectTimesheetInput: { id: timesheetId } });
      return res['rejectTimesheet']['timesheet'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Send a request for approve timesheet.
   *
   * @param id - Id for approve timesheet.
   * @param timesheetId
   */
  async approve(timesheetId: number): Promise<any> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation approveTimesheet($ApproveTimesheetInput: ApproveTimesheetInput!) {
            approveTimesheet(input: $ApproveTimesheetInput) {
              timesheet {
                id
                rate
                deliveredHours
                clientFirstApproved
                consumer {
                  uuid
                }
                agreement {
                  uuid
                  otherUser {
                    uuid
                  }
                }
              }
            }
          }
        `,
        { ApproveTimesheetInput: { id: timesheetId } },
      );
      return res['approveTimesheet']['timesheet'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a request to fetch timesheet details.
   *
   * @param id - Id for get data for particular timesheet.
   */
  async getById(id: number): Promise<Timesheet> {
    try {
      const res = await this.api.gqlQuery(gql`
        query eventDelivery($id: ID!){
          eventDelivery(id:$id) {
            id
            agreement {
              id
              otherUser {
                fullName
                firstName
                id
                ndisAgencyManaged
                disablePerKmTravel
              }
              agreementRates {
                rateCents
                flatRate
                rateName
                id
              }
              travelExpenseFlag
              quickpayEnabled
            }
            consumer {
              isHcpFunded
              id
              uuid
              fullName
              clientAccountType
              participants {
                fullName
                firstName
              }
              firstName
              organisation {
                businessName
              }
            }
            shiftNote {
              description
              canShareShiftNote
              sharedContactsCount
              sharedContacts {
                name
              }
              attachments {
                canDeleteFlag
                images {
                  asset {
                    url
                  }
                  category
                  createdAt
                  deletedAt
                  deletionReason
                  id
                  updatedAt
                }
              }
            }
            actualState
            carerPaidAt
            carerPaidByPaynow
            collectedAt
            canQuery
            carerPaidStatus
            deliveredHoursAndMinutes
            deliveredStartTime
            deliveredEndTime
            deliveryDate
            feePercentage
            invoiceCarerAdvanced
            paynowApprovedAt
            paynowAvailable
            paynowRequested
            paynowRequestedStatus
            paynowRequestedAt
            paynowDeclinedAt
            perKmRateAvailable
            state
            showIncidentToClient
            workerPaymentQueriedAt

            incidentReport {
              incidentActionsTake
              incidentAlreadyNotified
              incidentDate
              incidentDetail
              incidentInjuriesDamage
              incidentLocation
              incidentShortQuestionnaire
              incidentStepsPrevent
              incidentTime
              incidentWhat
              incidentWho
              incidentWitness
              incidentReportToOrg
            }
            rate
            rateKindLabel
            total
            netHourlyRate
            hourlyFee
            canDeleteAssets
            paynowFeePercentage
            paynowDisplayable
            approvedAt
            paymentTooltip
            consumerPaidAt
            canEditApproved
            deletable
            carerInvoiceStatementId
            hourlyPaynowFee
            deliveredCost
            totalFee
            agreementRateId
            isPending
            warnEdit
            hasIncident
            paymentDueByDate
            horizonOneOfferApprovedAt
            horizonOneOfferDeclinedAt
            createdAt
            horizonOfferStatus
            horizonOfferDeclinedReason
            horizonOfferWarningReason
            withInfoBanner
            invoicedAt
            quickpayEligible
            quickpayScheduledDate
            kmTravelled
            perKmRateInDollars
            totalTravelledInDollars
            totalTravelExpense
            totalTravelFee
            totalWithTravelCost
            supportHealthChange {
              id
              shareHealthChangeInformation
              healthChangeAdditionalDetails
              supportHealthChangeAnswers{
                supportNoteQuestionnaireId
                supportNoteQuestion
                answer
              }
              noObservation
            }
            supportHealthChangeCheckedQuestions
          }
        }`, { id });
      return res['eventDelivery'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends a GEt request to check if worker can create a timesheet or not.
   *
   */
  async canCreate(): Promise<boolean> {
    try {
      const res = await (<any>(await this.api.get('/api/v1/event_deliveries/status.json', true)).toPromise());
      return res?.data?.attributes?.status?.can_create;
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }

  /**
   * Get list of info banner for timesheet.
   *
   */
  async getTimesheetInfoBannerList(): Promise<any> {
    try {
      const res = await (<any>(await <any> this.api.get('/api/v1/event_deliveries/info_banners', true)).toPromise());
      return res.data;
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }

  /**
   * Post request for dismiss info banner list.
   *
   */
  async dismissTimesheetInfoBannerList(): Promise<any> {
    try {
      const res = await (<any>(await <any> this.api.post('/api/v1/event_deliveries/info_banners/dismiss', null, true)).toPromise());
      return res;
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }

  /**
   * Sends a request to fetch support health change questionnaires.
   *
   * @param fundingType - Client funding type.
   */
  async getSupportHealthChangeQuestionnaires(fundingType: 'hcp' | 'ndis'): Promise<SupportHealthChangeQuestionnaire[]> {
    try {
      const res = await this.api.gqlQuery(gql`
      query supportHealthChangeQuestionnaires($fundingType: SupportNoteQuestionnaireFunding!) {
        supportHealthChangeQuestionnaires(funding:$fundingType) {
          id
          question
          active
        }
      }`, { fundingType });
      return res['supportHealthChangeQuestionnaires'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends request to add health or mood change.
   *
   * @param healthChangeRequest - Health change request object
   * @returns Health change object
   */
  async addSupportHealthChangeAnswers(healthChangeRequest: HealthChangeRequest): Promise<SupportHealthChange> {
    try {
      const res = await this.api.gqlMutation(gql`
        mutation createSupportHealthChangeAnswers($healthChangeInput: CreateSupportHealthChangeAnswersInput!) {
          createSupportHealthChangeAnswers(input: $healthChangeInput) {
            supportHealthChange {
              id
              healthChangeAdditionalDetails
              shareHealthChangeInformation
              supportHealthChangeAnswers {
                id
                supportNoteQuestion
                answer
              }
            }
          }
        }`, { healthChangeInput: healthChangeRequest });
      return res['createSupportHealthChangeAnswers']['supportHealthChange'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends request to update health or mood change.
   *
   * @param healthChangeRequest - Health change request object
   * @returns Health change object
   */
  async updateSupportHealthChangeAnswers(healthChangeRequest: HealthChangeRequest): Promise<SupportHealthChange> {
    try {
      const res = await this.api.gqlMutation(gql`
        mutation updateSupportHealthChangeAnswers($healthChangeInput: UpdateSupportHealthChangeAnswersInput!) {
          updateSupportHealthChangeAnswers(input: $healthChangeInput) {
            supportHealthChange {
              id
              healthChangeAdditionalDetails
              shareHealthChangeInformation
              supportHealthChangeAnswers {
                id
                supportNoteQuestion
                answer
              }
            }
          }
        }`, { healthChangeInput: healthChangeRequest });
      return res['updateSupportHealthChangeAnswers']['supportHealthChange'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch unapproved support hours with health change count.
   */
  async fetchUnapprovedSupportHoursWithHealthChangeCount(): Promise<number> {
    try {
      const res = await this.api.gqlQuery(
        gql`
        query {
          timesheets{
            totalUnapproveWithHealthChange
          }
        }`
      );

      return res['timesheets']?.totalUnapproveWithHealthChange;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * This checks if the user has an existing timesheet regardless of the state.
   *
   * @returns - A Promise of Boolean. TRUE if the user has an existing timesheet regardless of the status.
   */
  async hasAtLeastOneTimeSheet(): Promise<boolean> {
    try {
      const res = await this.api.gqlQuery(
        gql`
        query {
          eventDeliveries(first: 1) {
            nodes {
              id
            }
          }
        }`,
      );

      return !!res['eventDeliveries']?.nodes?.length;
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
